import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, CheckSquare, AlertCircle, Clock } from "lucide-react";

/**
 * TaskFormModal - Modal for creating or editing a task
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onSubmit - Callback when form is submitted with task data
 * @param {string} projectId - ID of the project this task belongs to
 * @param {Object} taskToEdit - Optional existing task for edit mode
 */
const TaskFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  projectId,
  taskToEdit = null,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "to_do",
    priority: "medium",
    due_date: "",
    assigned_to: "",
    estimated_hours: "",
    started_at: "",
    completed_at: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  // Fill form with existing task data when editing
  useEffect(() => {
    if (taskToEdit) {
      setFormData({
        name: taskToEdit.name || "",
        description: taskToEdit.description || "",
        status: taskToEdit.status || "to_do",
        priority: taskToEdit.priority || "medium",
        due_date: formatDateForInput(taskToEdit.due_date),
        assigned_to: taskToEdit.assigned_to || "",
        estimated_hours: taskToEdit.estimated_hours || "",
        started_at: formatDateForInput(taskToEdit.started_at),
        completed_at: formatDateForInput(taskToEdit.completed_at),
      });
    } else {
      // Reset form when adding new task
      setFormData({
        name: "",
        description: "",
        status: "to_do",
        priority: "medium",
        due_date: "",
        assigned_to: "",
        estimated_hours: "",
        started_at: "",
        completed_at: "",
      });
    }
  }, [taskToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare data for submission
      const dataToSubmit = {
        ...formData,
        project_id: projectId,
      };

      // Handle empty optional fields -- send null instead of ""
      if (!dataToSubmit.estimated_hours) {
        dataToSubmit.estimated_hours = null;
      }
      if (!dataToSubmit.due_date) {
        dataToSubmit.due_date = null;
      }
      if (!dataToSubmit.assigned_to) {
        dataToSubmit.assigned_to = null;
      }
      if (!dataToSubmit.started_at) {
        delete dataToSubmit.started_at;  // let backend auto-set
      }
      if (!dataToSubmit.completed_at) {
        delete dataToSubmit.completed_at;  // let backend auto-set
      }

      await onSubmit({
        taskId: taskToEdit?._id,
        taskData: dataToSubmit,
      });
      onClose();
    } catch (error) {
      console.error("Error submitting task:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-grayteck-900 bg-opacity-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="relative bg-white dark:bg-darkbg-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-grayteck-500 hover:text-grayteck-700 dark:text-grayteck-400 dark:hover:text-grayteck-200"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Modal header */}
          <div className="flex items-center mb-6">
            <div className="mr-3 flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-grayteck-100 dark:bg-darkbg-700">
              <CheckSquare className="h-6 w-6 text-grayteck-600 dark:text-grayteck-400" />
            </div>
            <h3 className="text-lg font-medium text-grayteck-900 dark:text-grayteck-100">
              {taskToEdit ? "Edit Task" : "Create New Task"}
            </h3>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Task Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
              >
                Task Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                         rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                         focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                placeholder="Enter task name"
              />
            </div>

            {/* Task Description */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                         rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                         focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                placeholder="Describe the task"
              />
            </div>

            {/* Status and Priority Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Status */}
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Status *
                </label>
                <select
                  id="status"
                  name="status"
                  required
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                           rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                           focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                >
                  <option value="to_do">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="blocked">Blocked</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label
                  htmlFor="priority"
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Priority *
                </label>
                <select
                  id="priority"
                  name="priority"
                  required
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                           rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                           focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Due Date */}
            <div>
              <label
                htmlFor="due_date"
                className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
              >
                Due Date
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-grayteck-400" />
                </div>
                <input
                  id="due_date"
                  name="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={handleChange}
                  className="w-full pl-10 px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                           rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                           focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                />
              </div>
            </div>

            {/* Started At / Completed At (edit mode only) */}
            {taskToEdit && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="started_at"
                    className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                  >
                    Started At
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-grayteck-400" />
                    </div>
                    <input
                      id="started_at"
                      name="started_at"
                      type="date"
                      value={formData.started_at}
                      onChange={handleChange}
                      className="w-full pl-10 px-3 py-2 border border-grayteck-300 dark:border-grayteck-700
                               rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500
                               focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                    />
                  </div>
                  <p className="mt-1 text-xs text-grayteck-400">Auto-set when moved to In Progress</p>
                </div>
                <div>
                  <label
                    htmlFor="completed_at"
                    className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                  >
                    Completed At
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-grayteck-400" />
                    </div>
                    <input
                      id="completed_at"
                      name="completed_at"
                      type="date"
                      value={formData.completed_at}
                      onChange={handleChange}
                      className="w-full pl-10 px-3 py-2 border border-grayteck-300 dark:border-grayteck-700
                               rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500
                               focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                    />
                  </div>
                  <p className="mt-1 text-xs text-grayteck-400">Auto-set when moved to Completed</p>
                </div>
              </div>
            )}

            {/* Assigned To */}
            <div>
              <label
                htmlFor="assigned_to"
                className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
              >
                Assigned To
              </label>
              <input
                id="assigned_to"
                name="assigned_to"
                type="text"
                value={formData.assigned_to}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                         rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                         focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                placeholder="Name of person assigned"
              />
            </div>

            {/* Estimated Hours */}
            <div>
              <label
                htmlFor="estimated_hours"
                className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
              >
                Estimated Hours
              </label>
              <input
                id="estimated_hours"
                name="estimated_hours"
                type="number"
                min="0"
                step="0.25"
                value={formData.estimated_hours}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                         rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                         focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                placeholder="Estimated hours to complete"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-grayteck-200 dark:border-grayteck-700">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-700 dark:text-grayteck-300 
                       rounded-md hover:bg-grayteck-200 dark:hover:bg-darkbg-600 focus:outline-none"
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 bg-grayteck-600 text-white rounded-md hover:bg-grayteck-700 focus:outline-none
                        flex items-center justify-center min-w-[100px] ${
                          isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                        }`}
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : taskToEdit ? (
                  "Update Task"
                ) : (
                  "Create Task"
                )}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TaskFormModal;
